'use client';

import { useQuery } from '@tanstack/react-query';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Trans } from '@kit/ui/trans';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { Eyebrow } from '~/(tenant)/_components/native/_primitives';

import type { GreetingSectionConfig } from '~/(tenant)/_lib/schema/native-home-config.schema';
import type { GreetingPrefetch } from '~/(tenant)/_lib/server/load-greeting-data';

interface GreetingSectionProps {
  config: GreetingSectionConfig;
  accountId: string;
  /**
   * Server-prefetched greeting data. When supplied, React Query is seeded
   * synchronously and the section renders the final state on first paint —
   * no "Welcome." → "Welcome back, X." flash.
   */
  prefetch?: GreetingPrefetch | null;
}

/**
 * Editorial greeting block. Time-of-day eyebrow, serif welcome with the
 * user's first name italicized, and an optional credits/tier line.
 *
 * For unauthenticated users, falls back to a generic welcome.
 */
export function GreetingSection({
  config,
  accountId,
  prefetch,
}: GreetingSectionProps) {
  const t = useNativeTokens();
  const client = useSupabase();

  const prefetchedUserId = prefetch?.userId;

  // When prefetch supplied a userId, skip the client-side session probe.
  // Otherwise (unauth render or no prefetch), fall back to a session check.
  const { data: session } = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      const { data } = await client.auth.getSession();
      return data.session;
    },
    staleTime: 60_000,
    enabled: !prefetchedUserId,
  });

  const userId = prefetchedUserId ?? session?.user?.id;

  const { data: profile } = useQuery({
    queryKey: ['greeting-profile', userId, accountId],
    queryFn: async () => {
      if (!userId) return null;
      const { data } = await client
        .from('accounts')
        .select('name')
        .eq('id', userId)
        .maybeSingle();
      return data;
    },
    enabled: !!userId,
    staleTime: 5 * 60_000,
    initialData: prefetch?.firstName
      ? { name: prefetch.firstName }
      : undefined,
  });

  const firstName = (profile?.name ?? '').trim().split(/\s+/)[0];
  const eyebrow = greetingForHour(new Date().getHours());

  return (
    <section
      style={{
        padding: '32px 22px 0',
      }}
    >
      <Eyebrow style={{ marginBottom: 8 }}>
        <Trans i18nKey={eyebrow} />
      </Eyebrow>
      <h1
        style={{
          fontFamily: t.fontDisplay,
          fontSize: 32,
          lineHeight: 1.1,
          color: t.foreground,
          fontWeight: 300,
          letterSpacing: '-0.01em',
          margin: 0,
        }}
      >
        {firstName ? (
          <Trans
            i18nKey="common:nativeHome.welcomeBackNamed"
            values={{ name: firstName }}
            components={{ em: <em style={{ fontStyle: 'italic' }} /> }}
          />
        ) : (
          <Trans i18nKey="common:nativeHome.welcome" />
        )}
      </h1>

      {(config.showCredits || config.showTier) && userId && (
        <CreditsAndTier
          userId={userId}
          accountId={accountId}
          showCredits={config.showCredits}
          showTier={config.showTier}
          initialData={
            prefetch
              ? { credits: prefetch.credits, tier: prefetch.tier }
              : undefined
          }
        />
      )}
    </section>
  );
}

function CreditsAndTier({
  userId,
  accountId,
  showCredits,
  showTier,
  initialData,
}: {
  userId: string;
  accountId: string;
  showCredits?: boolean;
  showTier?: boolean;
  initialData?: { credits: number; tier: string | null };
}) {
  const t = useNativeTokens();
  const client = useSupabase();

  const { data } = useQuery({
    queryKey: ['greeting-membership', userId, accountId],
    initialData,
    queryFn: async () => {
      // Cast to any: these tables exist at runtime but the generated
      // Database type may not have them in this codepath.
      const c = client as any;
      const nowIso = new Date().toISOString();
      const [creditsResult, membershipResult] = await Promise.all([
        showCredits
          ? c
              .from('user_booking_packages')
              .select('remaining_booking_count')
              .eq('user_id', userId)
              .eq('account_id', accountId)
              .eq('status', 'active')
              .gt('expires_at', nowIso)
          : Promise.resolve({ data: null }),
        showTier
          ? c
              .from('user_memberships')
              .select('membership_plan:plan_id(name)')
              .eq('user_id', userId)
              .eq('account_id', accountId)
              .eq('status', 'active')
              .eq('is_deleted', false)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);

      const creditsRows = (creditsResult.data ?? []) as Array<{
        remaining_booking_count?: number | null;
      }>;
      const credits = creditsRows.reduce(
        (sum, row) => sum + (row.remaining_booking_count ?? 0),
        0,
      );

      const tier =
        (membershipResult.data as { membership_plan?: { name?: string } } | null)
          ?.membership_plan?.name ?? null;

      return { credits, tier };
    },
    staleTime: 60_000,
  });

  if (!data) return null;
  if ((!showCredits || data.credits === 0) && !data.tier) return null;

  return (
    <p
      style={{
        fontFamily: t.fontSans,
        fontSize: 13,
        color: t.muted,
        marginTop: 10,
        lineHeight: 1.5,
      }}
    >
      {showCredits && data.credits > 0 && (
        <Trans
          i18nKey="common:nativeHome.creditsRemaining"
          values={{ count: data.credits }}
          components={{
            strong: (
              <span style={{ color: t.foreground, fontWeight: 500 }} />
            ),
          }}
        />
      )}
      {showCredits && data.credits > 0 && showTier && data.tier && ' · '}
      {showTier && data.tier && (
        <Trans
          i18nKey="common:nativeHome.tierLine"
          values={{ tier: data.tier }}
          components={{
            strong: (
              <span style={{ color: t.foreground, fontWeight: 500 }} />
            ),
          }}
        />
      )}
    </p>
  );
}

function greetingForHour(hour: number): string {
  if (hour < 5) return 'common:nativeHome.greetingLateNight';
  if (hour < 12) return 'common:nativeHome.greetingMorning';
  if (hour < 17) return 'common:nativeHome.greetingAfternoon';
  if (hour < 21) return 'common:nativeHome.greetingEvening';
  return 'common:nativeHome.greetingNight';
}
